import { useTranslation } from "react-i18next"
import WhiteSpace from "../../components/Whitespace"
import PageTitle from "../../components/PageTitle"
import styled from "styled-components";
import { useEffect, useState } from "react";
import Comm from "../../Comm";
import InputWithIcon from "../../components/InputWithIcon";
import ChargerType from "../../types/ChargerType";
import Icon from "../../Icon";
import normalizeString from "../../utils/normalizeString";
import UpgradeHistoryType from "../../types/UpgradeHistoryType";
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"

const UpgradesContainer = styled.div`
  width: 100%;
  height: calc(90vh - 170px);
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    max-height: calc(100dvh - 160px);
    overscroll-behavior: contain;
  }
  border-radius: 16px;
  overflow-y: scroll;
`

const UpgradeContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  padding: 24px;
`

const UpgradeType = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-left: 24px;
`

const UpgradeBody = styled.p`
  /* File names are single unbreakable tokens; without this they run off the card. */
  overflow-wrap: anywhere;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-left: 24px;
  padding-top: 12px;
  line-height: 22px;
`

const UpgradeTime = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_600};
  margin: 0px;
  margin-left: 24px;
  padding-top: 12px;
`

const ToolbarContaier = styled.div`
  width: 100%;
  display: flex;
`

const RefreshButtonContainer = styled.div`
  background-image: linear-gradient(to right, ${props => props.theme.colors.successActive}, ${props => props.theme.colors.success});
  border-radius: 6px;
  padding: 8px;
  display: flex;
  cursor: pointer;
  float: left;
  margin-left: 24px;

  &:active {
    transform: scale(.98);
  }

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    align-items: center;
  }
`

const RefreshButtonLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.white};
  margin-bottom: 0px;
  margin-top: 0px;
  margin-left: 6px;
  margin-right: 6px;
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 32px;
  margin-left: 24px;
  text-align: center;
`

export default function UpgradeHistory({ charger }: { charger: ChargerType }) {
  const { t } = useTranslation()

  const [upgradeHistory, setUpgradeHistory] = useState<UpgradeHistoryType[]>()
  const [lastRefresh, setLastRefresh] = useState<string>('')
  const [searchResults, setSearchResults] = useState<UpgradeHistoryType[] | null>(null)

  async function fetchUpgradeHistory() {
    try {
      const response = await Comm('/api-firmware/v2/update/history', {
        headers: { "charger_id": charger.chargerId }
      })
      const result = await response.json()
      setUpgradeHistory(result.updateHistory)
      setLastRefresh(new Date().toString().split(' ')[4])
    } catch (e) { console.log(e) }
  }
  useEffect(() => {
    fetchUpgradeHistory()
  }, [])

  function handelRefresh() {
    setUpgradeHistory([])
    fetchUpgradeHistory()
  }

  function handleSearch(input: string) {
    if (upgradeHistory) {
      if (input != '') {
        const results: UpgradeHistoryType[] = []
        for (const upgrade of upgradeHistory) {
          const searchString = `${upgrade.version} ${upgrade.softwareName} ${upgrade.date} ${upgrade.contentType == 1 ? "SW" : upgrade.contentType == 2 ? "FW" : upgrade.contentType == 3 ? "OS" : "-"}`
          if (normalizeString(searchString.toLocaleLowerCase()).includes(normalizeString(input.toLocaleLowerCase()))) {
            results.push(upgrade)
          }
        }
        if (results.length < 1) {
          setSearchResults([])
        } else {
          setSearchResults(results)
        }
      } else {
        setSearchResults(null)
      }
    }
  }

  return (
    <>
      <WhiteSpace height={'18px'} />
      <PageTitle label={t('charger_upgradeHistory')} />
      <WhiteSpace height={'12px'} />
      <ToolbarContaier>
        <div>
          <RefreshButtonContainer onClick={handelRefresh}>
            <div style={{ marginLeft: "6px" }}>
              <Icon path="general.refresh" color="white" width="16px" height="16px" />
            </div>
            <RefreshButtonLabel>{t('logs_refresh')}</RefreshButtonLabel>
          </RefreshButtonContainer>
          <WhiteSpace height={'6px'} />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width={'42px'} />
        <InputWithIcon onInput={(input) => handleSearch(input)} width="max(12vw, 154px)" placeholder={t('logs_search')} icon="general.search" iconColor="gray_600" />
      </ToolbarContaier>
      <WhiteSpace height={'12px'} />
      <UpgradesContainer>
        <table style={{ width: '100%', tableLayout: 'fixed' }}>
          <tbody>
            {upgradeHistory && (searchResults ? searchResults : upgradeHistory).map((upgrade) => (
              <tr key={upgrade.date}>
                <td>
                  <UpgradeContainer>
                    <UpgradeType>{upgrade.contentType == 1 ? "SW" : upgrade.contentType == 2 ? "FW" : upgrade.contentType == 3 ? "OS" : "-"}</UpgradeType>
                    <UpgradeBody>
                      File:            {upgrade.softwareName}
                      <br />
                      Version:    {upgrade.version}
                      <br />
                      State:         {upgrade.stateName}
                    </UpgradeBody>
                    <UpgradeTime>{upgrade.date}</UpgradeTime>
                  </UpgradeContainer>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </UpgradesContainer>
    </>
  )
}